<!--
  Универсальный CRUD-компонент справочника с плоской структурой.
  По ТЗ «Рабочие задачи» блок 6.2–6.5: страницы Ranks/Positions/STPN/UserCategory
  и справочники КПС (VehicleType, MealType, CheckType) — все одного типа
  (id + name + опционально пара доп. полей). Один компонент — все страницы.
-->
<template>
  <section class="page">
    <div class="page__head">
      <h2 class="page__title">{{ title }}</h2>
      <div class="page__head-actions">
        <ActionButton :color="'#2A8A1B'" :text="addButton" @click="onAdd" />
        <div class="page__search">
          <input
            class="page__search-input"
            v-model="q"
            type="text"
            :placeholder="$t('settings.simple.searchBy', { field: primaryLabel })"
          />
        </div>
      </div>
    </div>

    <div v-if="error" class="dict__error">{{ error }}</div>

    <table class="dict__table">
      <thead>
        <tr>
          <th class="dict__col-id">{{ $t('common.id') }}</th>
          <th v-for="f in fields" :key="f.key">{{ f.label }}</th>
          <th class="dict__col-actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!filteredRows.length && !loading">
          <td :colspan="fields.length + 2" class="dict__empty">{{ $t('settings.simple.notFound') }}</td>
        </tr>
        <tr v-for="row in filteredRows" :key="row.id">
          <td class="dict__col-id">{{ row.id }}</td>
          <td v-for="f in fields" :key="f.key">
            <template v-if="!row.__editing">{{ row[f.key] || '—' }}</template>
            <input
              v-else
              class="dict__input"
              v-model="row[f.key]"
              :type="f.type || 'text'"
              :maxlength="f.maxlength || 128"
              :placeholder="f.placeholder || ''"
            />
          </td>
          <td class="dict__col-actions">
            <div class="actions">
              <template v-if="!row.__editing">
                <button class="actions__btn actions__btn--edit" @click="onEdit(row)" :title="$t('common.edit')" :aria-label="$t('common.edit')">
                  <img class="actions__icon" src="@/assets/images/edit.svg" alt="edit" />
                </button>
                <button class="actions__btn actions__btn--delete" @click="onDelete(row)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
                  <img class="actions__icon" src="@/assets/images/markdown/trash.svg" alt="delete" />
                </button>
              </template>
              <template v-else>
                <button class="actions__btn actions__btn--accept" @click="onSave(row)" :title="$t('common.save')" :aria-label="$t('common.save')">
                  <img class="actions__icon" src="@/assets/images/accept.svg" alt="accept" />
                </button>
                <button class="actions__btn actions__btn--decline" @click="onCancel(row)" :title="$t('common.cancel')" :aria-label="$t('common.cancel')">
                  <img class="actions__icon" src="@/assets/images/decline.svg" alt="decline" />
                </button>
              </template>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script>
import ActionButton from '@/components/widgets/ActionButton.vue'
import { http } from '@/api/http'

export default {
  name: 'SimpleDictView',
  components: { ActionButton },
  props: {
    // Например: 'ranks/', 'positions/', 'users/stpn-types/', 'warehouse/vehicle-types/'
    endpoint: { type: String, required: true },
    title: { type: String, required: true },
    addButton: { type: String, default: '' },
    // [{ key: 'name', label: 'Название', type?: 'text', maxlength?: 128 }, ...]
    fields: { type: Array, required: true },
  },
  data() {
    return {
      rows: [],
      q: '',
      loading: false,
      error: null,
    }
  },
  computed: {
    primaryLabel() {
      return (this.fields[0]?.label || this.$t('common.name')).toLowerCase()
    },
    filteredRows() {
      const qq = this.q.trim().toLowerCase()
      if (!qq) return this.rows
      return this.rows.filter(r =>
        this.fields.some(f => String(r[f.key] || '').toLowerCase().includes(qq))
      )
    },
  },
  async mounted() {
    await this.fetchRows()
  },
  methods: {
    async fetchRows() {
      this.loading = true
      this.error = null
      try {
        const { data } = await http.get(this.endpoint)
        const list = Array.isArray(data) ? data : (data.results || [])
        this.rows = list.map(r => ({ ...r, __editing: false, __backup: null, __new: false }))
      } catch (e) {
        this.error = e?.message || this.$t('settings.simple.loadError')
      } finally {
        this.loading = false
      }
    },
    onAdd() {
      const draft = { id: null, __editing: true, __new: true, __backup: null }
      this.fields.forEach(f => { draft[f.key] = '' })
      this.rows = [draft, ...this.rows]
    },
    onEdit(row) {
      row.__backup = { ...row }
      row.__editing = true
    },
    onCancel(row) {
      if (row.__new) {
        this.rows = this.rows.filter(r => r !== row)
        return
      }
      if (row.__backup) Object.assign(row, row.__backup)
      row.__editing = false
      row.__backup = null
    },
    payloadFor(row) {
      const out = {}
      this.fields.forEach(f => { out[f.key] = row[f.key] })
      return out
    },
    async onSave(row) {
      const payload = this.payloadFor(row)
      // Простая валидация: первое поле обязательно
      const first = this.fields[0]
      if (!String(payload[first.key] || '').trim()) {
        this.error = this.$t('settings.simple.fieldRequired', { field: first.label })
        return
      }
      this.error = null
      try {
        if (row.__new) {
          const { data } = await http.post(this.endpoint, payload)
          Object.assign(row, data)
          row.__new = false
        } else {
          const { data } = await http.patch(`${this.endpoint}${row.id}/`, payload)
          Object.assign(row, data)
        }
        row.__editing = false
        row.__backup = null
      } catch (e) {
        this.error = e?.response?.data?.detail || e?.message || this.$t('settings.simple.saveError')
      }
    },
    async onDelete(row) {
      const name = row[this.fields[0].key]
      if (!confirm(this.$t('settings.simple.confirmDelete', { name }))) return
      try {
        await http.delete(`${this.endpoint}${row.id}/`)
        this.rows = this.rows.filter(r => r !== row)
      } catch (e) {
        this.error = e?.response?.data?.detail || e?.message || this.$t('settings.simple.deleteError')
      }
    },
  },
}
</script>

<style scoped>
.page {
  padding: 24px 24px 40px;
  background: #FFFFFF;
  border-radius: 16px;
  min-height: 100%;
}
.page__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}
.page__title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}
.page__head-actions { display: flex; gap: 12px; align-items: center; }
.page__search {
  width: 260px;
  padding: 6px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
}
.page__search-input { border: none; outline: none; width: 100%; font-size: 14px; }

.dict__error {
  padding: 10px 14px;
  background: #FEE2E2;
  color: #991B1B;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
}
.dict__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}
.dict__table thead th {
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #E5E7EB;
  background: #F9FAFB;
  font-weight: 700;
  color: #374151;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.dict__table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #F3F4F6;
  color: #111827;
}
.dict__col-id { width: 60px; color: #6B7280; }
.dict__col-actions { width: 100px; text-align: right; white-space: nowrap; }
.dict__empty { text-align: center; color: #6B7280; padding: 30px !important; }
.dict__input {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
}
.dict__input:focus { outline: 2px solid rgba(47, 120, 238, .25); border-color: #2F78EE; }
</style>
